import {
  Assignee,
  Attachment,
  BellRing,
  FlagPriorityOutline
} from "@dreamhub/designsystem/icons";
import DataTable from "@/components/controls/DataTable/DataTable";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import AssigneeIndicator from "@/components/controls/Task/AssigneeIndicator";
import AttachmentsIndicator from "@/components/controls/Task/AttachmentsIndicator";
import CreatedByDreamerIndication from "@/components/controls/Task/CreatedByDreamerIndication";
import CreatedByUserIndication from "@/components/controls/Task/CreatedByUserIndication";
import DueDateIndicator from "@/components/controls/Task/DueDateIndicator";
import HighPriorityIndicator from "@/components/controls/Task/HighPriorityIndicator";
import RemindersIndicator from "@/components/controls/Task/RemindersIndicator";
import UserAvatar from "@/components/controls/UserAvatar";
import EntitiesCell from "@/components/layouts/TasksFocus/EntitiesCell";
import Text from "@/components/ui/text";
import {
  DeleteTaskModalState,
  TaskData,
  TaskModalState,
  TaskState
} from "@/entities/tasks/types";
import { resolveTaskEntities } from "@/entities/tasks/helpers";
import { getTaskEntityNames } from "@/entities/tasks/taskLinks";
import { CellContext, ColumnDef, Row, SortingState } from "@tanstack/react-table";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getEnhancedRelativeTime } from "@/utils/datetime";
import Checkmark from "@/components/ui/checkmark";
import { cn } from "@/utils/utils";
import HeaderWithTooltip from "@/components/layouts/TasksFocus/HeaderWithTooltip";
import {
  getDefaultSortField,
  useTasksListData
} from "@/entities/tasks/useTasksListData";
import { Filters } from "@/services/api/types";
import { capitalize, find, isNull, upperFirst } from "lodash";
import EmptyTasksState from "@/components/layouts/TasksDrawer/EmptyTasksState";
import { ColumnData } from "@/entities/dataTables/types";
import { UserData } from "@/entities/users/types";
import { TFunction } from "i18next";
import { mergeColumnsWithDefaults } from "@/entities/dataTables/helpers";
import { getDefaultTaskColumnsState } from "@/entities/tasks/helpers";

const TRANSLATION_KEY = "focus.tasks";
const DEFAULT_TABLE_ID = "tasks-focus-table";

interface CreatedByColumnArgs {
  users: UserData[];
  userId: string | undefined;
  t: TFunction<"translation", undefined>;
}

// Kept out of the component per the repo's TanStack column-def convention; it
// closes over nothing, so its inputs are passed explicitly.
const buildCreatedByColumn = ({ users, userId, t }: CreatedByColumnArgs) => ({
  id: "createdBy",
  accessorKey: "createdBy",
  header: t(`${TRANSLATION_KEY}.table.createdBy`),
  cell: (info: CellContext<TaskData, any>) => {
    const task = info.row.original;
    const createdById = info.getValue();
    const createdByData = find(users, (user) => user.id === createdById);
    if (task?.isCreatedByDreamer) {
      const assigneeData = task.assigneeId
        ? find(users, (user) => user.id === task.assigneeId)
        : null;
      // Names the source popover needs; the chip's own "for <user>" suffix stays off.
      const contextOwnerName = createdById
        ? (createdByData?.displayName ?? null)
        : null;
      const assigneeName =
        userId && assigneeData?.id === userId
          ? t("you")
          : (assigneeData?.displayName ?? null);
      // The column header already reads "Created by", so the chip drops that prefix.
      return (
        <CreatedByDreamerIndication
          task={task}
          label={t("dreamer")}
          hideForUserSuffix
          contextOwnerName={contextOwnerName}
          contextOwnerUserData={createdByData ?? null}
          assigneeName={assigneeName}
        />
      );
    }
    const createdByName =
      createdById === userId
        ? capitalize(t("you"))
        : createdByData?.displayName;
    return (
      <div className="flex min-w-0 items-center gap-2">
        <UserAvatar userData={createdByData} size="M" className="shrink-0" />
        {createdByName && (
          <Text variant="smBodySecondary" className="truncate min-w-0">
            {createdByName}
          </Text>
        )}
      </div>
    );
  },
  size: 130
});

interface Props {
  selectedLayoutTab: string;
  selectedTab: string;
  allTasksFilters: Filters;
  assignedToMeFilters: Filters;
  assignedByMeFilters: Filters;
  searchTerm: string;
  userId: string | undefined;
  setTaskModalState: React.Dispatch<React.SetStateAction<TaskModalState>>;
  setDeleteTaskModalState: React.Dispatch<
    React.SetStateAction<DeleteTaskModalState>
  >;
  // Lets a host page (e.g. the full-page Tasks view) drive sorting/columns
  // from its own toolbar. Falls back to internal state for the widget.
  sorting?: SortingState;
  onSortingChange?: React.Dispatch<React.SetStateAction<SortingState>>;
  columnsState?: ColumnData[];
  tableId?: string;
  height?: string;
  className?: string;
  // Column resize/reorder/sticky-left are full-page-only affordances added
  // alongside the toolbar-driven sorting/columns above. The embedded widget
  // never exposed them and doesn't have the room for a resize/reorder UX.
  columnCustomizationEnabled?: boolean;
}

const TasksTable: React.FC<Props> = ({
  selectedLayoutTab,
  selectedTab,
  allTasksFilters,
  assignedToMeFilters,
  assignedByMeFilters,
  searchTerm,
  userId,
  setTaskModalState,
  setDeleteTaskModalState,
  sorting: externalSorting,
  onSortingChange: externalOnSortingChange,
  columnsState,
  tableId = DEFAULT_TABLE_ID,
  height = "40vh",
  className,
  columnCustomizationEnabled = true
}) => {
  const { t } = useTranslation();

  const [internalSorting, internalOnSortingChange] =
    React.useState<SortingState>(() => {
      return [
        {
          id: getDefaultSortField(selectedTab),
          desc: false
        }
      ];
    });

  const sorting = externalSorting ?? internalSorting;
  const onSortingChange = externalOnSortingChange ?? internalOnSortingChange;

  const {
    tasks,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    users,
    taskAnimationStates,
    handleLoadMore,
    handleCheckedChange
  } = useTasksListData({
    selectedLayoutTab,
    selectedTab,
    allTasksFilters,
    assignedToMeFilters,
    assignedByMeFilters,
    searchTerm,
    sorting,
    enabled: Boolean(userId)
  });

  const previousSelectedTabRef = React.useRef(selectedTab);

  useEffect(() => {
    if (previousSelectedTabRef.current === selectedTab) {
      return;
    }
    previousSelectedTabRef.current = selectedTab;
    onSortingChange([
      {
        id: getDefaultSortField(selectedTab),
        desc: false
      }
    ]);
  }, [selectedTab, onSortingChange]);

  const columns = useMemo(() => {
    const leadingColumnDefs: ColumnDef<TaskData, any>[] = [
      {
        id: "isCompleted",
        accessorKey: "isCompleted",
        header: () => null,
        enableSorting: false,
        cell: (info) => {
          const id = info.row.original?.id;
          const isCompleted = info.getValue();
          const animationState = taskAnimationStates[id];
          const displayIsCompleted =
            animationState?.localIsCompleted ?? isCompleted;

          return (
            <div className="flex w-full justify-center">
              <Checkmark
                id={`isCompleted-${id}`}
                checked={displayIsCompleted}
                disabled={animationState?.isAnimating}
                onCheckedChange={(checked) => handleCheckedChange(id, checked)}
                tooltipText={t(
                  `tasks.drawer.task.${displayIsCompleted ? "markAsOpen" : "markAsComplete"}`
                )}
              />
            </div>
          );
        },
        meta: {
          textAlign: "center",
          paddingLeft: "pl-4",
          hideSeparator: true,
          disableDragging: true
        },
        size: 50,
        minSize: 50,
        maxSize: 50
      },
      {
        id: "title",
        accessorKey: "title",
        header: t(`${TRANSLATION_KEY}.table.title`),
        cell: (info) => {
          const title = info.getValue() || t("notFound");
          const task = info.row.original;
          const isCreatedByDreamer = task?.isCreatedByDreamer;
          const createdById = task?.createdBy;
          const assigneeId = task?.assigneeId;
          const isNotCreatedByUser = createdById && createdById !== userId;
          const createdByUserData = createdById
            ? find(users, (user) => user.id === createdById)
            : null;
          const assigneeData = assigneeId
            ? find(users, (user) => user.id === assigneeId)
            : null;
          const taskId = task?.id;
          const animationState = taskAnimationStates[taskId];
          const showStrikethrough = animationState?.showStrikethrough ?? false;

          // Pre-resolve names so CreatedByDreamerIndication needs no useUsers
          // subscription and doesn't cause re-renders on user-store updates.
          const dreamerContextOwnerName =
            isCreatedByDreamer && createdById
              ? createdByUserData?.displayName ?? null
              : null;
          const dreamerAssigneeName =
            userId && assigneeData?.id === userId
              ? t("you")
              : assigneeData?.displayName ?? null;

          return (
            <div className="flex flex-col gap-1">
              <Text
                variant="body"
                className={cn(
                  "transition-all duration-300 truncate",
                  showStrikethrough && "line-through opacity-60"
                )}
                as="span"
              >
                {title}
              </Text>
              {/* Only the widget shows the creator here; the full page has a Created by column. */}
              {!columnCustomizationEnabled && isCreatedByDreamer && (
                <CreatedByDreamerIndication
                  task={task}
                  contextOwnerName={dreamerContextOwnerName}
                  contextOwnerUserData={createdByUserData ?? null}
                  assigneeName={dreamerAssigneeName}
                />
              )}
              {!columnCustomizationEnabled &&
                !isCreatedByDreamer &&
                isNotCreatedByUser &&
                createdByUserData && (
                  <CreatedByUserIndication
                    createdByUserData={createdByUserData}
                  />
                )}
            </div>
          );
        },
        size: 150
      }
    ];

    const toggleableColumnDefs: ColumnDef<TaskData, any>[] = [
      {
        id: "entityNames",
        header: t(`${TRANSLATION_KEY}.table.entityNames`),
        cell: ({ row }) => {
          const entities = resolveTaskEntities(
            getTaskEntityNames(row.original.entityNames, row.original.entities),
            row.original.entityIds
          );
          if (entities.length > 0) {
            return <EntitiesCell entities={entities} />;
          }
        },
        enableSorting: false,
        size: 150
      },
      ...(selectedTab === TaskState.OPEN
        ? [
            {
              id: "dueDate",
              accessorKey: "dueDate",
              header: t(`${TRANSLATION_KEY}.table.dueDate`),
              cell: (info: CellContext<TaskData, any>) => (
                <DueDateIndicator
                  tooltip={t("tasks.drawer.task.tooltips.dueDate")}
                  dueDate={info.getValue()}
                  hideTooltip
                  className="w-fit p-0"
                />
              ),
              sortingFn: "datetime" as const,
              size: 50
            }
          ]
        : [
            {
              id: "completedAt",
              accessorKey: "completedAt",
              header: t(`${TRANSLATION_KEY}.table.completedAt`),
              cell: (info: CellContext<TaskData, any>) => {
                const completedAt = info.getValue();
                return completedAt ? (
                  <Text>
                    {upperFirst(getEnhancedRelativeTime(t, completedAt))}
                  </Text>
                ) : (
                  t("tables.notAvailable")
                );
              },
              sortingFn: "datetime" as const,
              size: 50
            }
          ]),
      {
        id: "isHighPriority",
        accessorKey: "isHighPriority",
        header: () => (
          <HeaderWithTooltip
            icon={FlagPriorityOutline as any}
            tooltip={t(`${TRANSLATION_KEY}.table.isHighPriority`)}
          />
        ),
        cell: (info) => {
          const isHighPriority = info.getValue();
          if (isHighPriority) {
            return (
              <div className="flex w-[80%] justify-center">
                <HighPriorityIndicator
                  tooltip={t("tasks.drawer.task.tooltips.highPriority")}
                  className="w-fit"
                />
              </div>
            );
          }
        },
        meta: {
          textAlign: "center"
        },
        size: 40
      },
      {
        id: "reminders",
        accessorKey: "reminders",
        header: () => (
          <HeaderWithTooltip
            icon={BellRing as any}
            tooltip={t(`${TRANSLATION_KEY}.table.reminders`)}
          />
        ),
        cell: (info: CellContext<TaskData, any>) => {
          const reminders = info.getValue();
          if (!isNull(reminders) && reminders?.items?.length > 0) {
            return (
              <div className="flex w-full justify-center">
                <RemindersIndicator reminders={reminders} className="w-fit" />
              </div>
            );
          }
        },
        meta: {
          textAlign: "center"
        },
        enableSorting: false,
        size: 40
      },
      {
        id: "attachments",
        accessorKey: "attachments",
        header: () => (
          <HeaderWithTooltip
            icon={Attachment as any}
            tooltip={t(`${TRANSLATION_KEY}.table.attachments`)}
          />
        ),
        cell: (info) => {
          const attachments = info.getValue();
          if (!isNull(attachments) && attachments?.items?.length > 0) {
            return (
              <div className="flex w-full justify-center">
                <AttachmentsIndicator
                  attachments={attachments}
                  className="w-fit"
                />
              </div>
            );
          }
        },
        meta: {
          textAlign: "center"
        },
        enableSorting: false,
        size: 40
      },
      {
        id: "assigneeId",
        accessorKey: "assigneeId",
        header: () => (
          <HeaderWithTooltip
            icon={Assignee as any}
            tooltip={t(`${TRANSLATION_KEY}.table.assigneeId`)}
          />
        ),
        cell: (info) => {
          const assigneeData = find(
            users,
            (user) => user.id === info.getValue()
          );
          return (
            <div className="flex w-[75%] justify-center">
              <AssigneeIndicator
                assigneeData={assigneeData}
                className="w-fit"
              />
            </div>
          );
        },
        meta: {
          textAlign: "center"
        },
        size: 40
      },
      // Created-by is a full-page-only column added alongside the toolbar
      // customization affordances above; the legacy widget never had it.
      ...(columnCustomizationEnabled
        ? [buildCreatedByColumn({ users, userId, t })]
        : []),
      ...(selectedTab === TaskState.COMPLETED
        ? [
            {
              id: "completedBy",
              accessorKey: "completedBy",
              header: t(`${TRANSLATION_KEY}.table.completedBy`),
              cell: (info: CellContext<TaskData, any>) => {
                const completedBy = info.getValue();
                const completedByData = find(
                  users,
                  (user) => user.id === completedBy
                );
                const completedByName =
                  completedBy === userId
                    ? capitalize(t("you"))
                    : completedByData?.displayName;
                return completedByName ? (
                  <Text>{completedByName}</Text>
                ) : (
                  t("tables.notAvailable")
                );
              },
              size: 50
            }
          ]
        : [])
    ];

    const trailingColumnDefs: ColumnDef<TaskData, any>[] = [
      {
        id: "id",
        accessorKey: "id",
        header: () => null,
        enableSorting: false,
        cell: (info) => {
          const taskId = info.getValue();
          const taskName = info.row.original.title;
          return (
            <div>
              <OptionsDropdownMenu
                options={{
                  edit: () =>
                    setTaskModalState({
                      editTaskId: taskId,
                      editTaskData: info.row.original,
                      isEditMode: true,
                      isOpen: true
                    }),
                  delete: () =>
                    setDeleteTaskModalState({
                      isOpen: true,
                      task: {
                        id: taskId,
                        name: taskName
                      }
                    })
                }}
                tPrefix={`${TRANSLATION_KEY}.options`}
                buttonVariant="iconNoBg"
                iconColor="secondary"
                className="hover:!bg-white-4"
              />
              <Text className="sr-only">
                {t(`${TRANSLATION_KEY}.table.actions`)}
              </Text>
            </div>
          );
        },
        meta: {
          textAlign: "center"
        },
        size: 30
      }
    ];

    // Toggleable columns are shown/reordered per the Display panel's column
    // state; when none is provided (e.g. the legacy widget) all show in their
    // default order.
    const defaultColumnsState = getDefaultTaskColumnsState(t, selectedTab);
    const mergedColumnsState = mergeColumnsWithDefaults(
      columnsState,
      defaultColumnsState
    );
    const toggleableById = new Map(
      toggleableColumnDefs.map((col) => [col.id, col])
    );
    const orderedToggleableColumnDefs = mergedColumnsState
      .filter((col) => col.isVisible && toggleableById.has(col.id))
      .sort((a, b) => a.order - b.order)
      .map((col) => toggleableById.get(col.id) as ColumnDef<TaskData, any>);

    return [
      ...leadingColumnDefs,
      ...orderedToggleableColumnDefs,
      ...trailingColumnDefs
    ];
  }, [
    selectedTab,
    taskAnimationStates,
    userId,
    users,
    t,
    columnsState,
    columnCustomizationEnabled,
    handleCheckedChange,
    setTaskModalState,
    setDeleteTaskModalState
  ]);

  const emptyState = searchTerm ? (
    <div className="flex items-center justify-center h-72">
      <Text variant="bodySecondary">{t("noResultsFound")}</Text>
    </div>
  ) : (
    <EmptyTasksState className="h-72" />
  );

  return (
    <DataTable
      dataTest={tableId}
      columns={columns}
      initialSortBy={sorting}
      manualSorting
      onSortingChange={onSortingChange}
      data={tasks}
      headerClassName={
        columnCustomizationEnabled
          ? "border-none"
          : "border-b border-border-border1 bg-surface-elevation1"
      }
      stickyLeftColumn={columnCustomizationEnabled}
      resizableColumns={columnCustomizationEnabled}
      enableColumnReorder={columnCustomizationEnabled}
      height={height}
      className={className}
      isInfiniteScroll
      isLoadingMore={isFetchingNextPage}
      isDataLoading={isLoading}
      hasMore={hasNextPage && !isError}
      onLoadMore={handleLoadMore}
      loadingThreshold={100}
      tableId={tableId}
      getRowClassName={(row) =>
        taskAnimationStates[row.original?.id]?.isAnimating
          ? "animate-bounce-out-down"
          : undefined
      }
      onRowClick={(row: Row<any>) =>
        setTaskModalState({
          editTaskId: row.original.id,
          editTaskData: row.original,
          isEditMode: true,
          isOpen: true
        })
      }
      customEmptyState={emptyState}
    />
  );
};

export default TasksTable;
